import {useState} from "react";
import {useParams, useHistory} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, CircularProgress, TextField} from "@mui/material";
import BigSelector, {BigSelectorItem} from "../../components/common/BigSelector/BigSelector";
import {useAdminApi, ADMIN_NAMESPACES_KEY, ADMIN_ACCOUNTS_KEY} from "./api";

export function NamespaceDetails() {
    var {key} = useParams<{key: string}>();
    var history = useHistory();
    var api = useAdminApi();
    var queryClient = useQueryClient();

    var {data: namespaces, isLoading: loadingNamespaces} = useQuery(
        [ADMIN_NAMESPACES_KEY],
        () => api.listNamespaces(),
        {staleTime: 30000}
    );

    var {data: accounts, isLoading: loadingAccounts} = useQuery(
        [ADMIN_ACCOUNTS_KEY],
        () => api.listAccounts(),
        {staleTime: 30000}
    );

    var namespace = namespaces?.find(ns => ns.key === key);

    var [name, setName] = useState<string | null>(null);
    var [saving, setSaving] = useState(false);
    var [error, setError] = useState<string | null>(null);

    if (loadingNamespaces) {
        return <div style={{display: "flex", justifyContent: "center", padding: 32}}><CircularProgress/></div>;
    }

    if (!namespace) {
        return <Alert severity="error" sx={{m: 2}}>Namespace "{key}" not found</Alert>;
    }

    var editName = name ?? namespace.name;

    async function handleSave() {
        try {
            setSaving(true);
            setError(null);
            await api.updateNamespace(key, editName);
            await queryClient.invalidateQueries([ADMIN_NAMESPACES_KEY]);
            history.push("/admin/namespaces");
        } catch (e: any) {
            setError(e.message || "Failed to update namespace");
        } finally {
            setSaving(false);
        }
    }

    var relatedAccounts = (accounts || []).filter(a => a.namespace === key);

    var accountItems: BigSelectorItem[] = relatedAccounts.map(account => ({
        id: account.key,
        name: account.key,
        subtitle: account.name || "(no name)",
        icon: <i className="fas fa-building"/>,
        onActivate: () => history.push(`/admin/accounts/${account.key}`),
    }));

    return (
        <div>
            <Button startIcon={<i className="fas fa-arrow-left"/>} onClick={() => history.push("/admin/namespaces")} sx={{mb: 2}}>
                Back
            </Button>

            <h2>Namespace: {key}</h2>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <div style={{maxWidth: 400, marginBottom: 24}}>
                <TextField
                    margin="dense"
                    label="Name"
                    fullWidth
                    variant="outlined"
                    value={editName}
                    onChange={(e) => setName(e.target.value)}
                    sx={{mb: 2}}
                />
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </div>

            <BigSelector
                title="Accounts"
                items={accountItems}
                loading={loadingAccounts}
                emptyMessage="No accounts in this namespace"
            />
        </div>
    );
}
